/* eslint-disable react-hooks/exhaustive-deps */
import { ExperimentConfiguration } from 'models';
import { useState, createContext, FC, useContext, useCallback, useEffect, SetStateAction } from 'react';
import Papa from 'papaparse';
import {
	ExperimentResults,
	GoToNextStage,
	StimulusData,
	HeaderToIndexMap,
	SrcToAudioMap,
	BlockResult,
	TrialRunnerStage,
	Stage,
	AudioPresentation,
	ViolationLog
} from 'types';
import { getAudioFilePath, getExpDirPath } from 'utility';
import { getExperimentContent, getExperimentSettings, getExperimentStimulusList } from './ExperimentAPI';

interface Props {
	experimentName: string | undefined;
	stimList?: string | null;
	pcode?: string | null;
}

const DEFAULT_STIMULI: string = 'stimuli';
localStorage.removeItem("QUALTRICS_TRANSCRIPT_BASE");
localStorage.removeItem("QUALTRICS_TRANSCRIPT");


const defaultSrcAudioMap: SrcToAudioMap = new Map<string, HTMLAudioElement>([
	['$spark', new Audio('/templates/audio/spark.mp3')],
	['$lefttone', new Audio('/templates/audio/lefttone.mp3')],
	['$righttone', new Audio('/templates/audio/righttone.mp3')],
	['$first', new Audio('/templates/audio/snd1.ogg')],
	['$second', new Audio('/templates/audio/snd2.ogg')],
	['$third', new Audio('/templates/audio/snd3.ogg')],
]);

const useValue = ({ experimentName, stimList, pcode }: Props) => {
	const [expConfig, setExpConfig] = useState<ExperimentConfiguration>();
	const [contentString, setContentString] = useState<string | null>();
	const [content, setContent] = useState<HTMLElement>();
	const [srcToAudioMap, setSrcToAudioMap] = useState<SrcToAudioMap>(defaultSrcAudioMap);
	const [currentStageIndex, setCurrentStageIndex] = useState<number>();
	const [collectionName, setCollectionName] = useState<string>();
	const [fileNameColumn, setFileNameColumn] = useState<string>();
	const [pcodeurl, setpcodeurl] = useState<string | null | undefined>(pcode);
	const [stimListName, setStimListName] = useState<string>();
	const [currentStage, setCurrentStage] = useState<Stage>();
	const [experimentStartTime,setExperimentTime] = useState(0);
	const [experimentStartDateTime] = useState(`${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`);
	const [stimulusHeader, setStimulusHeader] = useState<string[]>();
	const [stimulusData, setStimulusData] = useState<StimulusData>();
	const [headerToIndexMap, setHeaderToIndexMap] = useState<HeaderToIndexMap>();
	const [experimentResults, setExperimentResults] = useState({} as ExperimentResults);
    const [violationLogs, setExperimentViolationLogs] = useState<ViolationLog[] | undefined>(undefined);
	const [activeTrialIdentifier, setActiveTrialIdentifier] = useState<string | number | null>(null);

	const getSettings = useCallback(async () => {
		try {
			const res = await getExperimentSettings(experimentName!);
			var defaultStimulis: string = DEFAULT_STIMULI
			if(typeof res.defaultStimulusList !== 'undefined'){
				defaultStimulis = res.defaultStimulusList;
			}
			//console.log(res)
			//console.log(defaultStimulis)
			setExpConfig(new ExperimentConfiguration(res, experimentName!, stimList ?? defaultStimulis));
		} catch (e) {
			console.log(e);
			throw new Error('No expConfig found');
		}
	}, [setExpConfig, experimentName, stimList]);

	const getContentFile = useCallback(async () => {
		try {
			const res = await getExperimentContent(experimentName!);
			setContentString(res);
		} catch {
			setContentString(null);
		}
	}, [setContentString, experimentName]);

	const processTrialRunnerConfiguration = useCallback(
		async (config: Stage | undefined) => {
			if (config) {
				const trialRunnerConfig = config as TrialRunnerStage;
				const res = await getExperimentStimulusList(experimentName!, trialRunnerConfig.stimuliSource!);
				const { data } = Papa.parse<string[]>(res);
				const headerRow: string[] = data.shift()!;
				setStimulusHeader(headerRow);
				setStimListName(trialRunnerConfig.stimuliSource);
				const blockedData: StimulusData = [];
				data.forEach((row) => {
					const blockNumberString = row[0] as string;
					const blockNumber = Number.parseInt(blockNumberString);
					const block = blockedData[blockNumber];

					if (!block) {
						blockedData[blockNumber] = [row];
					} else {
						block.push(row);
					}
				});
				setStimulusData(blockedData);

				const tempHeaderToIndexMap: HeaderToIndexMap = new Map();

				headerRow.forEach((columnHeader, columnIndex) => tempHeaderToIndexMap.set(columnHeader, columnIndex));

				setHeaderToIndexMap(tempHeaderToIndexMap);

				const audioPresentations = trialRunnerConfig.presentations.reduce((arr, presentation) => {
					if (presentation.type === 'audio') {
						arr.push(presentation);
					} else if (presentation.type === 'concurrent') {
						arr.push(
							...(presentation.presentations.filter(
								(pres) => pres.type === 'audio'
							) as AudioPresentation[])
						);
					}
					return arr;
				}, [] as AudioPresentation[]);

				// add each audio file to the audio map
				const tempSrcToAudioMap: SrcToAudioMap = new Map();
				audioPresentations.forEach((audioPresentation) => {
					data.forEach((row) => {
						const audioColumnIndex = tempHeaderToIndexMap.get(audioPresentation.fileNameColumn);
						if (!audioColumnIndex) throw new Error();

						const fileName = row[audioColumnIndex];
						if (fileName) {
							tempSrcToAudioMap.set(fileName, new Audio(getAudioFilePath(experimentName!, fileName)));
						}
					});
				});
				setSrcToAudioMap((prevSrcToAudioMap) => new Map([...prevSrcToAudioMap, ...tempSrcToAudioMap]));
			}
		},
		[experimentName]
	);

	useEffect(() => {
		getSettings();
		getContentFile();
	}, [getSettings, getContentFile]);

	useEffect(() => {
		setContent(new DOMParser().parseFromString(contentString ?? '', 'text/html').documentElement);
	}, [contentString]);

	useEffect(() => {
		if (expConfig) {
			setCurrentStageIndex(expConfig.firstStageIndex);
			setCurrentStage(expConfig.stages[expConfig.firstStageIndex]);
			setCollectionName(expConfig.collectionName);
			setFileNameColumn(expConfig.fileNameColumn);
			console.log(expConfig);
			processTrialRunnerConfiguration(expConfig.stages.find((stage: Stage) => stage.type === 'trialRunner'));
		}
		// If this returns true, a bot script is currently driving the browser
		if (navigator.webdriver) {
		    const violationObj = {
		        type: "automated_browser_detected",
		        timestamp: new Date().toLocaleTimeString(),
		        message: "Failed navigator.webdriver check. Likely a bot/script."
		    };
		    const existingLogs = (window as any).SCREEN_VIOLATIONS || [];
		    existingLogs.push(violationObj);
		    (window as any).SCREEN_VIOLATIONS = existingLogs;
		}
	}, [expConfig, processTrialRunnerConfiguration]);

	useEffect(() => {
		if (expConfig && currentStageIndex !== undefined) {
			const newStage = expConfig.stages[currentStageIndex];
			setCurrentStage(newStage);
		}
	}, [expConfig, currentStageIndex]);

// --- NEW: Define this synchronously OUTSIDE the useEffect ---
	const isTranscriptionEnabled = (expConfig as any)?.enableTranscription === true;
	(window as any).globalTranscriptionEnabled = isTranscriptionEnabled;
	// ------------------------------------------------------------

	useEffect(() => {
            // Safely scan the JSON sequence to see if ANY stage requires the microphone
            // (Adjust .stages to .sequence or .timeline if your framework uses a different array name)
            const experimentStages = (expConfig as any)?.stages || (expConfig as any)?.sequence || [];
            const needsMic = experimentStages.some((stage: any) => stage.enableTranscription === true);

            if (needsMic) {
                navigator.mediaDevices.getUserMedia({ audio: true })
                    .then((stream) => {
                        stream.getTracks().forEach(track => track.stop());
                        console.log("Microphone permission pre-approved by participant.");
                    })
                    .catch((err) => console.warn("Mic access denied.", err));
            }
    }, [expConfig]);

	// --- FULL SCREEN ON STARTUP & VIOLATION TRACKER ---
	useEffect(() => {
		const shouldFullScreen = (expConfig as any)?.fullScreenOnStartup === true;

		if (shouldFullScreen) {
			const enableFullScreen = () => {
				if (!document.fullscreenElement) {
					document.documentElement.requestFullscreen().catch((err) => {
						console.warn("Could not enable full-screen mode:", err.message);
					});
				}
				document.removeEventListener('click', enableFullScreen);
			};

			// Listen for the first click to start full-screen
			document.addEventListener('click', enableFullScreen);

			// --- NEW: Watch for the Escape key ---
			const catchFullScreenExit = () => {
				// If this is null, it means the browser is no longer in full-screen
				if (!document.fullscreenElement) {
					const timeOfViolation = new Date().toLocaleTimeString();
					// Create a clean, JSON-friendly object
					const violationObj = {
						event: "fullscreen_exit",
						time: `${timeOfViolation}`
					};

					//console.warn("🚨 VIOLATION LOGGED:", violationObj);

					// Attach this to the global window so SaveData.ts can grab it,
					// or push it to your framework's existing violation tracker!
					const existingLogs = (window as any).SCREEN_VIOLATIONS || [];
					existingLogs.push(violationObj);
					(window as any).SCREEN_VIOLATIONS = existingLogs;
				}
			};

			document.addEventListener('fullscreenchange', catchFullScreenExit);

			// Cleanup listeners if the component unmounts
			return () => {
				document.removeEventListener('click', enableFullScreen);
				document.removeEventListener('fullscreenchange', catchFullScreenExit);
			};
		}
	}, [expConfig]);


// --- OUT OF BOUNDS & FOCUS LOSS TRACKER ---
	useEffect(() => {
		// --- NEW: Read the config array ---
		const focusAlertConfig = (expConfig as any)?.browserFocusAlert;

		// Check if it exists, is an array, and if the first item is true
		const shouldTrackFocus = Array.isArray(focusAlertConfig) && focusAlertConfig[0] === true;

		if (shouldTrackFocus) {
			let violationCount = 0;

			// --- NEW: Set threshold from the second item in the array (fallback to 3 if missing) ---
			const THRESHOLD = (typeof focusAlertConfig[1] === 'number') ? focusAlertConfig[1] : 3;
			let lastViolationTime = 0;

			const handleViolation = (violationType: string, baseMessage: string) => {
				const now = Date.now();
				if (now - lastViolationTime < 500) return;
				lastViolationTime = now;

				violationCount++;

				const timeOfViolation = new Date().toLocaleTimeString();
				const violationObj = {
					type: violationType,
					timestamp: timeOfViolation,
					message: `${baseMessage} (Count: ${violationCount})`
				};

				const existingLogs = (window as any).SCREEN_VIOLATIONS || [];
				existingLogs.push(violationObj);
				(window as any).SCREEN_VIOLATIONS = existingLogs;

				if (violationCount >= THRESHOLD) {
					showWarningOverlay();
					violationCount = 0; // Reset the counter after each warning
				}
			};

			const onMouseLeave = () => handleViolation("mouse_out_of_bounds", "Mouse left window");
			const onWindowBlur = () => handleViolation("window_focus_lost", "Window lost focus");

			const showWarningOverlay = () => {
				if (document.getElementById('experiment-warning-overlay')) return;

				const overlay = document.createElement('div');
				overlay.id = 'experiment-warning-overlay';
				overlay.innerText = "⚠️ Please keep your mouse inside the experiment window.";

				Object.assign(overlay.style, {
					position: 'fixed',
					top: '20px',
					left: '50%',
					transform: 'translateX(-50%)',
					backgroundColor: 'red',
					color: 'white',
					padding: '20px',
					fontSize: '24px',
					fontWeight: 'bold',
					zIndex: '9999',
					borderRadius: '8px',
					boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
					pointerEvents: 'none'
				});

				document.body.appendChild(overlay);

				setTimeout(() => {
					overlay.remove();
				}, 3000);
			};

			document.addEventListener('mouseleave', onMouseLeave);
			window.addEventListener('blur', onWindowBlur);

			return () => {
				document.removeEventListener('mouseleave', onMouseLeave);
				window.removeEventListener('blur', onWindowBlur);
			};
		}
	}, [expConfig]);
	/**
	 * Determines what stage to go to next.
	 *
	 * @param goodOrBad Optional. If good, go to experiment nextStageIfYes. If bad, go to nextStageIfNo.
	 */
	const goToNextStage: GoToNextStage = useCallback(
		(endPrematurely: boolean = false) => {
			if (expConfig) {
				if (currentStageIndex! + 1 >= expConfig.stages.length) {
					// There are no more stages. Restarts the experiment right now.
					window.location.href = getExpDirPath(experimentName!);
				} else {
					// There are more stages
					setCurrentStageIndex((currentStageIndex) => {
						if (!endPrematurely) {
							// Just go to the next stage chronologically

							return currentStageIndex! + 1;
						} else {
							const answer = expConfig.stages.findIndex(
								// @ts-ignore
								(stage) => stage.name === (currentStage.nextStageIfNo ?? 'prematureConclusion')
							);

							if (answer === -1) throw new Error('No stage with name "prematureConclusion"');

							return answer;
						}
					});
				}
			}
		},
		[expConfig, currentStage, currentStageIndex, experimentName]
	);

	const addBlockResults = (blockResults: BlockResult[]) => {
		setExperimentResults((prevExperimentResults) => ({ ...prevExperimentResults, blockResults }));
	};

	const updateExpeimentDuration = function (duration: SetStateAction<number>){
		setExperimentTime(duration);
	};

	return {
		stimulusData,
		stimulusHeader,
		experimentName,
		collectionName,
		pcodeurl,
		setpcodeurl,
		currentStage: currentStage!,

		experimentResults,
		stimListName,
		currentStageIndex,

		goToNextStage,
		expConfig,
		headerToIndexMap,
		srcToAudioMap,
		content,

		addBlockResults,
		updateExpeimentDuration,

		experimentStartTime,
		experimentStartDateTime,
		fileNameColumn,
        violationLogs,
		setExperimentViolationLogs,

		activeTrialIdentifier,
        setActiveTrialIdentifier,
	};
};

export const experimentContext = createContext({} as ReturnType<typeof useValue>);
export const useExperimentContext = () => useContext(experimentContext);

export const ExperimentContextProvider: FC<Props> = (props) => (
	<experimentContext.Provider value={useValue(props)}>{props.children}</experimentContext.Provider>
);
